import type { DocumentAllocationLike } from '@erp/domain/payments';
import { calculateRepresentativeStats } from './calculate-stats';
import type { RepresentativeStats, StatsDocument, StatsPeriod } from './types';

const EPSILON = 0.001;

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Solde de commission brut = bénéfice × taux/100.
 *
 * Formule pure sans notion de paiement : utile pour des calculs intermédiaires
 * ou pour afficher une estimation "théorique". Pour le solde réellement
 * crédité au portefeuille du représentant, utiliser
 * {@link calculateRepresentativeWalletBalance}.
 */
export function calculateRepresentativeCommission(
  stats: Pick<RepresentativeStats, 'profit'>,
  commissionRate: number,
): number {
  const rate = Number(commissionRate) || 0;
  const profit = Number(stats.profit) || 0;
  return round3(profit * (rate / 100));
}

function documentDueAmount(doc: StatsDocument): number {
  const net = Number(doc.netToPay) || 0;
  if (net > EPSILON) return net;
  const ttc = Number(doc.totalTtc) || 0;
  return ttc;
}

/**
 * Somme des allocations pointant vers `docId` **ou** vers un de ses parents.
 *
 * Dans cet ERP, un paiement est souvent saisi à la création du document
 * (le form envoie une allocation sur le BL, même si on crée une facture
 * directe). Et quand une facture est créée à partir d'un BL, le paiement
 * du BL doit "remonter" sur la facture pour créditer la commission.
 * On suit donc la chaîne parent.
 */
function sumAllocatedForDoc(
  doc: StatsDocument,
  allocations: DocumentAllocationLike[] | undefined | null,
): number {
  if (!allocations || allocations.length === 0) return 0;
  const ids = new Set<string>([doc.id, ...(doc.parentDocIds ?? [])]);
  let sum = 0;
  for (const a of allocations) {
    if (ids.has(a.documentId)) sum += Number(a.allocatedAmount) || 0;
  }
  return round3(sum);
}

function isFullyPaid(
  doc: StatsDocument,
  allocations: DocumentAllocationLike[] | undefined | null,
): boolean {
  const due = documentDueAmount(doc);
  const paid = sumAllocatedForDoc(doc, allocations);
  if (due <= EPSILON) {
    // Sans montant dû renseigné, on considère "payé" dès qu'il y a une
    // allocation non nulle — évite de bloquer la commission sur un doc
    // mal saisi côté totaux.
    return paid > EPSILON;
  }
  return paid + EPSILON >= due;
}

/**
 * Solde du portefeuille de commissions.
 *
 * Règles métier :
 * - seuls les documents **pleinement payés** comptent (tout-ou-rien, pas de
 *   prorata sur les paiements partiels) ;
 * - les BL non facturés sont exclus de fait (ils n'ont pas de paiement
 *   direct — c'est la facture qui est payée) ;
 * - les avoirs ne sont déduits que s'ils sont eux-mêmes "payés", c'est-à-dire
 *   remboursés au client (allocation de décaissement) ;
 * - la période filtre sur la date du document (pas sur la date de paiement),
 *   pour rester cohérent avec les autres KPI du représentant.
 */
export function calculateRepresentativeWalletBalance(
  documents: StatsDocument[],
  allocations: DocumentAllocationLike[] | undefined | null,
  commissionRate: number,
  period?: StatsPeriod,
): number {
  const paidDocs = documents.filter((d) => isFullyPaid(d, allocations));
  const paidStats = calculateRepresentativeStats(paidDocs, period);
  return calculateRepresentativeCommission(paidStats, commissionRate);
}
